import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { LifeActMenuLevel } from '@app/myWeeklyLifeActs/myLifeActsMenu/myLifeActsMenu.component';
import { ParticipantProgression, ParticipantService } from '@shared/services/participant/participant.service';
import { EntityStatus, Level, MenuModel, Module, ProgressionEntity } from '@shared/models/menu.model';
import { distinctUntilChanged, first, firstValueFrom, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { PlRouter } from '@shared/services/pl-router.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Breakpoint, WindowEventService } from '@shared/services/window.event.service';
import {
  ModalMyLifeActsBlocksComponent,
  ModalMyLIfeActsBlocksInputs,
} from '@app/myWeeklyLifeActs/modalMyLifeActsBlocks/modalMyLifeActsBlocks.component';
import { ZendeskService } from '@shared/services/zendesk.service';
import { ButtonVariant } from '@app/components/button/button.component';
import { getRoutePath, PlRoute } from '@app/utils/routes';
import { ApiUrlStep, CourseContentService } from '@app/courseContent/courseContent.service';
import { ErrorStepData, isStepData, StepData } from '@shared/models/step.model';

@Component({
  selector: 'pl-weekly-life-acts',
  templateUrl: './myWeeklyLifeActs.component.html',
})
export class MyWeeklyLifeActsComponent implements OnInit, OnDestroy {
  public lifeActMenu: Signal<LifeActMenuLevel[]> = computed(() =>
    this.computeLifeActMenu(this.participantService.menu()),
  );

  public participantHasUnlockAtLeastOneLifeActStep: Signal<boolean> = computed(() =>
    this.hasUnlockedAtLeastOneLifeActStep(this.participantService.participantProgression()),
  );

  public userIsOnSmallScreen: WritableSignal<boolean> = signal(false);

  public lifeactStepSelectedFullUrl: WritableSignal<string> = signal(``);

  private currentGetStepDataSubscription: Subscription | null = null;
  public currentlySelectedLifeactStepData: WritableSignal<StepData | null> = signal(null);
  public stepDataRetrieveInProgress: WritableSignal<boolean> = signal(false);

  private readonly URL_QUERY_PARAM_FULL_URL_SELECTED: string = 'lifeact' as const;

  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private courseContentService: CourseContentService,
    private dialog: MatDialog,
    private participantService: ParticipantService,
    private plRouter: PlRouter,
    private route: ActivatedRoute,
    private windowEventService: WindowEventService,
    private zendeskService: ZendeskService,
  ) {
    effect(
      () => {
        const currentlySelectedLifeactStepData: StepData | null = this.currentlySelectedLifeactStepData();
        if (currentlySelectedLifeactStepData) {
          this.lifeactStepSelectedFullUrl.set(currentlySelectedLifeactStepData.fullUrl);
        }
      },
      { allowSignalWrites: true },
    );

    this.courseContentService.hideFooter();
    this.courseContentService.hideCompanyBanner();

    this.windowEventService
      .getWindowResizeEvent()
      .pipe(distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((size: number): void => {
        this.userIsOnSmallScreen.set(size < Breakpoint.lg);
      });
  }

  public ngOnDestroy(): void {
    this.courseContentService.resetAllCustomisation();
  }

  public async ngOnInit(): Promise<void> {
    this.route.queryParams.pipe(first()).subscribe({
      next: async (queryParams: Params): Promise<void> => {
        const currentlySelectedMenuItemFullUrl: string | undefined =
          queryParams[this.URL_QUERY_PARAM_FULL_URL_SELECTED];
        if (currentlySelectedMenuItemFullUrl) {
          await this.displayLifeActStep(currentlySelectedMenuItemFullUrl);
        } else {
          for (const level of this.lifeActMenu()) {
            for (const module of level.modules) {
              if (module.statusLifeActStep === EntityStatus.current) {
                await this.displayLifeActStep(module.fullUrlLifeActStep);
              }
            }
          }
        }
      },
    });
  }

  public goToCurrentStep(): void {
    const participantProgression: ParticipantProgression | null = this.participantService.participantProgression();
    if (participantProgression) {
      const routeNextStep: string = participantProgression.linkNextStep || getRoutePath(PlRoute.home);
      this.plRouter.navigate([routeNextStep]);
    } else {
      throw new Error(`participantProgression  should be available`);
    }
  }

  public async displayLifeActStep(lifeactStepFullUrl: string): Promise<void> {
    if (lifeactStepFullUrl) {
      this.plRouter.navigate([], {
        queryParams: {
          [this.URL_QUERY_PARAM_FULL_URL_SELECTED]: lifeactStepFullUrl,
        },
        queryParamsHandling: 'merge',
      });

      if (this.userIsOnSmallScreen()) {
        this.openModalForSmallScreens(lifeactStepFullUrl);
      } else {
        this.stepDataRetrieveInProgress.set(true);
        this.currentlySelectedLifeactStepData.set(null);
        const stepData: StepData = await this.retrieveStepData(lifeactStepFullUrl);
        this.currentlySelectedLifeactStepData.set(stepData);
        this.stepDataRetrieveInProgress.set(false);
      }
    }
  }

  private retrieveStepData(lifeActStepFullUrl: string): Promise<StepData> {
    return new Promise<StepData>((resolve): void => {
      if (this.currentGetStepDataSubscription) {
        this.currentGetStepDataSubscription.unsubscribe();
      }

      this.currentGetStepDataSubscription = this.courseContentService
        .getStepData(ApiUrlStep.step, lifeActStepFullUrl, { slug: lifeActStepFullUrl })
        .pipe(first())
        .subscribe({
          next: (stepData: StepData | ErrorStepData): void => {
            if (isStepData(stepData)) {
              this.courseContentService.setCurrentStepData(stepData);
              resolve(stepData);
            } else {
              throw new Error(`Error retrieving lifeact steps`);
            }
          },
        });
    });
  }

  private async openModalForSmallScreens(currentlySelectedLifeactFullUrl: string): Promise<void> {
    this.lifeactStepSelectedFullUrl.set(currentlySelectedLifeactFullUrl);
    this.zendeskService.hideWidget();
    const dialogRef: MatDialogRef<ModalMyLifeActsBlocksComponent, undefined> = this.dialog.open<
      ModalMyLifeActsBlocksComponent,
      ModalMyLIfeActsBlocksInputs,
      undefined
    >(ModalMyLifeActsBlocksComponent, {
      data: {
        currentlySelectedLifeactFullUrl,
      },
      height: '100%',
      width: '100%',
      maxWidth: `100vw`,
    });

    await firstValueFrom(dialogRef.afterClosed());

    await this.plRouter.navigate([], {
      queryParams: {
        [this.URL_QUERY_PARAM_FULL_URL_SELECTED]: null,
      },
      queryParamsHandling: 'merge',
    });
    this.lifeactStepSelectedFullUrl.set('');
    this.zendeskService.showWidget();
  }

  private computeLifeActMenu(menu: MenuModel | null): LifeActMenuLevel[] {
    if (menu) {
      const currentLifeActStep: ProgressionEntity | undefined = menu.current_progression.current_lifeact_step;
      return menu.menudata[0].children.map((level: Level): LifeActMenuLevel => {
        const lifeActLevel: LifeActMenuLevel = {
          id: level.id,
          title: level.title,
          fullUrl: level.fullUrl,
          isDisabled: false,
          status: EntityStatus.locked,
          modules: [],
        };

        let allLifeActStepsWithinThisLevelAreCompleted: boolean = true;
        let currentLifeActStepIsWithinThisLevel: boolean = false;
        let allLifeActStepsWithinThisLevelAreLocked: boolean = true;
        level.children.forEach((module: Module): void => {
          for (const step of module.children) {
            if (step.category === 'lifeact') {
              const isLifeActStepCurrent: boolean = !!currentLifeActStep && currentLifeActStep.fullUrl === step.fullUrl;

              if (isLifeActStepCurrent) {
                currentLifeActStepIsWithinThisLevel = true;
              }

              if (step.status !== EntityStatus.locked) {
                allLifeActStepsWithinThisLevelAreLocked = false;
              }

              if (step.status !== EntityStatus.completed) {
                allLifeActStepsWithinThisLevelAreCompleted = false;
              }

              lifeActLevel.modules.push({
                isDisabled: step.status === EntityStatus.locked,
                title: `${lifeActLevel.modules.length + 1}: ${module.name}`,
                statusLifeActStep: isLifeActStepCurrent ? EntityStatus.current : step.status,
                fullUrlLifeActStep: step.fullUrl,
              });
              break;
            }
          }
        });

        if (currentLifeActStepIsWithinThisLevel) {
          lifeActLevel.status = EntityStatus.current;
        } else if (allLifeActStepsWithinThisLevelAreCompleted) {
          lifeActLevel.status = EntityStatus.completed;
        } else if (allLifeActStepsWithinThisLevelAreLocked) {
          lifeActLevel.status = EntityStatus.locked;
        }

        return lifeActLevel;
      });
    }

    return [];
  }

  private hasUnlockedAtLeastOneLifeActStep(participantProgression: ParticipantProgression | null): boolean {
    return !!participantProgression && !!participantProgression.linkNextLifeActs;
  }
}
